import React, { useState } from "react";

import { PaymentElement, useCheckout } from "@stripe/react-stripe-js";
import { PhoneNumberUtil } from "google-libphonenumber";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Trans } from "react-i18next";
import { PhoneInput } from "react-international-phone";

import { getUrlLangPrefix } from "@/utils/Local";
import {
  formatMoney,
  getReserveReturnUrl,
  isCompanyValid,
  validateEmail,
} from "@/utils/Toolbox";

import i18n from "../../i18n";
import apiService from "../../services/api";

import type { Layout, StripePaymentElementOptions } from "@stripe/stripe-js";

const paymentElementOptions = {
  layout: "tabs" as Layout,
} as StripePaymentElementOptions;

interface Contact {
  name: string;
  email: string;
  phone: string;
  company: string;
}

const ReserveCheckoutFormComponent: React.FC = () => {
  const { t } = i18n;
  const checkout = useCheckout();
  const langPrefix = getUrlLangPrefix();

  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  } as Contact);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const phoneUtil = PhoneNumberUtil.getInstance();

  const isPhoneValid = () => {
    try {
      return phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(contact.phone),
      );
    } catch (error) {
      return false;
    }
  };

  const isReserveButtonDisabled = () =>
    isLoading || !isPhoneValid() || !validateEmail(contact.email);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      await apiService.reserve(checkout.id, contact);
    } catch (error) {
      setMessage(t("_error_message"));
      return;
    }
    const updateEmailResult = await checkout.updateEmail(contact.email);
    if (updateEmailResult.type === "error") {
      setMessage(updateEmailResult.error.message);
      return;
    }

    const checkoutResult = await checkout.confirm({
      returnUrl: getReserveReturnUrl(),
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (checkoutResult.type === "error") {
      setMessage(checkoutResult.error.message);
    }

    setIsLoading(false);
  };

  const models = [
    {
      name: "PakAlps",
      price: 78000,
      size: {
        label: t("One or two bedroom house"),
        value: t("392 sq ft to 522 sq ft"),
      },
      lifeTime: 400,
      img: "/images/models/pak-alps/PakAlps_exterior05.jpg",
    },
    {
      name: "PakMonaco",
      price: 198000,
      size: {
        label: t("Two or three bedroom house"),
        value: t("1000 sq ft to 1500 sq ft"),
      },
      lifeTime: 400,
      img: "/images/models/pak-monaco/PakMonaco-exterior01.jpg",
    },
  ];

  return (
    <Container>
      <Row>
        <Col xs={12}>
          <h2>{t("Reserve Now")}</h2>
          <p>
            {t(
              "Reserve your place in line for $500.00 and join the innovators embracing PakVille’s sustainable, long-lasting homes. Your reservation fee is fully refundable until you confirm your order. Model selection comes later—no pressure.",
            )}
          </p>
        </Col>
        <Col xs={12}>
          <Row>
            {models.map((model) => (
              <Col key={model.name} md={6} xs={12}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>
                      <h2 className="modelHeader">{t(model.name)}</h2>
                    </Card.Title>

                    <Card.Text>
                      {t("starting_from_price", {
                        price: formatMoney(model.price),
                      })}
                    </Card.Text>

                    <Card.Text>
                      {model.size.label} <span>{model.size.value}</span>
                    </Card.Text>

                    <Card.Text>
                      {t("Life expectancy:")}{" "}
                      {t("+years", { years: model.lifeTime })}
                    </Card.Text>
                  </Card.Body>
                  <Card.Img alt={model.name} src={model.img} variant="bottom" />
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
        <Col xs={12}>
          <h3 className="mt-3 mb-3">
            {t("Reservation fee due today")}:{" "}
            {formatMoney(checkout.total.total.minorUnitsAmount / 100)}
          </h3>
          <form id="payment-form" onSubmit={handleSubmit}>
            <Card>
              <Card.Body>
                <Card.Title>{t("_checkout.contact_info_title")}</Card.Title>
                <Row>
                  <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                    <Form.Label htmlFor="name">{t("name")}</Form.Label>
                    <Form.Control
                      required
                      id="name"
                      isInvalid={!contact.name || contact.name === ""}
                      type="text"
                      value={contact.name}
                      onChange={(e) =>
                        setContact({ ...contact, name: e.target.value })
                      }
                    />
                    <Form.Text muted id="nameHelpBlock" />
                  </Col>

                  <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                    <Form.Label htmlFor="email">
                      {t("email_address")}
                    </Form.Label>
                    <Form.Control
                      required
                      id="email"
                      isInvalid={!validateEmail(contact.email)}
                      type="email"
                      value={contact.email}
                      onChange={(e) =>
                        setContact({ ...contact, email: e.target.value })
                      }
                    />
                    <Form.Text muted id="emailHelpBlock">
                      {t("We will send reservation updates to this email.")}
                    </Form.Text>
                  </Col>

                  <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                    <Form.Label htmlFor="phone">{t("phone_number")}</Form.Label>

                    <PhoneInput
                      forceDialCode
                      required
                      defaultCountry="ca"
                      inputClassName={`form-control ps-5 ${contact.phone && !isPhoneValid() ? "is-invalid" : ""}`}
                      onChange={(phone) => setContact({ ...contact, phone })}
                      placeholder=""
                      preferredCountries={["ca"]}
                      value={contact.phone}
                      countrySelectorStyleProps={{
                        buttonClassName: "m-0 p-0",
                        buttonStyle: {
                          height: "35px",
                          position: "absolute",
                          border: "none",
                          left: "14px",
                          top: "2px",
                        },
                        flagStyle: { height: "16px" },
                      }}
                      inputProps={{
                        name: "phone",
                        id: "phone",
                        autoComplete: "tel",
                      }}
                      inputStyle={{
                        height: "38px",
                        fontSize: "1rem",
                        borderRadius: "var(--bs-border-radius)",
                      }}
                    />

                    <Form.Text muted id="phoneHelpBlock" />
                  </Col>

                  <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                    <Form.Label htmlFor="company">
                      {t("company")} ({t("optional")})
                    </Form.Label>
                    <Form.Control
                      id="company"
                      type="text"
                      value={contact.company}
                      isInvalid={
                        contact.company !== undefined &&
                        !isCompanyValid(contact.company)
                      }
                      onChange={(e) =>
                        setContact({ ...contact, company: e.target.value })
                      }
                    />
                    <Form.Text muted id="companyHelpBlock" />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Body>
                <Card.Title>{t("_checkout.payment_options_title")}</Card.Title>
                <Row>
                  <Col xs={12}>
                    <PaymentElement
                      id="payment-element"
                      options={paymentElementOptions}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="mt-2">
              <Button
                disabled={isReserveButtonDisabled()}
                type="submit"
                variant="primary d-flex justify-content-center ms-auto"
              >
                {isLoading ? (
                  <React.Fragment>
                    <Spinner animation="border" className="p-2" size="sm" />
                    <span style={{ opacity: 0 }}>.</span>
                  </React.Fragment>
                ) : (
                  t("Reserve")
                )}
              </Button>

              {/* Show any error or success messages */}
              {message ? (
                <Alert
                  dismissible
                  className="d-flex gap-2 align-items-center mt-2"
                  variant="danger"
                >
                  {message}
                </Alert>
              ) : null}
            </div>
          </form>
        </Col>

        <Col className="mt-4 reservationNotesContainer" xs={12}>
          <Trans i18nKey="terms_message">
            <a
              href={`${langPrefix}/${t("_route.reservation-agreement")}`}
              rel="noreferrer"
              target="_blank"
            >
              {" "}
            </a>

            <a
              href={`${langPrefix}/${t("_route.terms-of-service")}`}
              rel="noreferrer"
              target="_blank"
            >
              {" "}
            </a>

            <a
              href={`${langPrefix}/${t("_route.privacy-policy")}`}
              rel="noreferrer"
              target="_blank"
            >
              {" "}
            </a>
          </Trans>

          <ol className="ps-3">
            <li>
              <strong>
                {t("Pricing applies to the Canadian market only.")}
              </strong>
              &nbsp;
              {t(
                "The listed price reflects the Manufacturer Suggested Retail Price (MSRP) and does not include taxes, permits, site prep, utility connections, delivery, installation or optional upgrades and premium features.",
              )}
            </li>

            <li>
              <strong>{t("Production of PakVille homes is underway.")}</strong>
              &nbsp;
              {t(
                "Availability and delivery timelines may vary depending on your model, location, and local permitting processes.",
              )}
            </li>
          </ol>
        </Col>
      </Row>
    </Container>
  );
};

export default ReserveCheckoutFormComponent;
